"use client";

import { EyeIcon, EyeOffIcon } from "lucide-react";
import * as React from "react";

import { Input, type InputProps } from "@/app/_components/ui/input";
import { cn } from "@/lib/utils";

/**
 * A password field with an eye button that shows or hides what was typed.
 * Every prop (including the `id` and aria attributes `FormControl` adds) goes
 * to the input itself, so labels, errors and password managers still find it.
 */
const PasswordInput = React.forwardRef<
  HTMLInputElement,
  Omit<InputProps, "type" | "enableCopy">
>(({ className, ...props }, ref) => {
  const [isShown, setIsShown] = React.useState(false);

  return (
    <div className="relative">
      <Input
        // Once shown as text, mobile keyboards would otherwise capitalize and
        // autocorrect the password.
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        {...props}
        ref={ref}
        type={isShown ? "text" : "password"}
        // `!` so this beats form-level padding such as the front door's px-4.
        // Edge draws its own reveal eye, which would sit under ours.
        className={cn(
          "!pr-12 [&::-ms-clear]:hidden [&::-ms-reveal]:hidden",
          className,
        )}
      />
      <button
        type="button"
        onClick={() => setIsShown((current) => !current)}
        aria-label={isShown ? "Hide password" : "Show password"}
        aria-controls={props.id}
        disabled={props.disabled}
        className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
      >
        {isShown ? (
          <EyeOffIcon className="h-5 w-5" aria-hidden="true" />
        ) : (
          <EyeIcon className="h-5 w-5" aria-hidden="true" />
        )}
      </button>
    </div>
  );
});
PasswordInput.displayName = "PasswordInput";

export { PasswordInput };
